/**
 * Pub/sub bus for OUTBOUND UiCmd frames, the mirror image of
 * `webSocketMessageBus` (which carries the inbound stream).
 *
 * `MonitoringWebSocket` calls `emitUiCmdTx()` immediately after
 * `socket.send()` returns without throwing, handing over the exact byte array
 * that went on the wire. The Terminal subscribes here so an operator can watch
 * their own commands leave, decoded, in the same table as the controller's
 * replies — the send path itself never learns who is listening.
 *
 * Zero overhead when nothing is subscribed: `emitUiCmdTx` early-returns before
 * touching the payload, so the common case (Terminal closed) does not pay for
 * the decode.
 */

import { logError } from "../../../utils/devLog";

/** One outbound frame, exactly as handed to `WebSocket.send`. */
export interface UiCmdTxFrameInput {
  /** The bytes that were sent. Treated as read-only by every listener. */
  payload: Uint8Array;
  /** Edge server the frame was sent to — matches `MonitoringWebSocket`'s prop. */
  serverId: string;
  /** Human-readable server name, for display alongside the row. */
  serverName: string;
  /** Channel the UiCmd was addressed to. */
  channelId: string;
  /** Wall-clock time of the send, milliseconds since epoch. */
  sentAt: number;
}

/**
 * A frame plus the send-ordering number the bus stamped on it. `seq` is
 * assigned here rather than by the caller because the bus is the only place
 * that sees every outbound frame in order — and because the Terminal needs a
 * key that is unique even for two identical commands sent in the same
 * millisecond, which a timestamp alone cannot give it.
 */
export interface UiCmdTxEvent extends UiCmdTxFrameInput {
  seq: number;
}

export type UiCmdTxListener = (event: UiCmdTxEvent) => void;

const listeners = new Set<UiCmdTxListener>();
let sequence = 0;

export function subscribeUiCmdTx(listener: UiCmdTxListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitUiCmdTx(frame: UiCmdTxFrameInput): void {
  if (listeners.size === 0) return;
  sequence += 1;
  const event: UiCmdTxEvent = { ...frame, seq: sequence };
  listeners.forEach((listener) => {
    // Guard each listener individually: this bus is driven from the WebSocket
    // send path, and a subscriber that throws must not surface there as a
    // failed command. Set.forEach would otherwise let the throw escape and
    // skip every remaining listener.
    try {
      listener(event);
    } catch (err) {
      logError("[uiCmdTxBus] listener threw:", err);
    }
  });
}

export function hasUiCmdTxListeners(): boolean {
  return listeners.size > 0;
}
